<template>
  <div class="flex flex-col px-4 pb-4 text-14">
    <p class="text-blue-900 font-semibold mb-1">
      {{ $t('promotion.promotion') }}
    </p>
    <p class="subtitle2">
      <strong v-html="$t(`promotions:${promotion.headline}`, promoVariables)" /><template
        v-if="promotion.conditions"
        >*</template
      >
    </p>
    <div
      v-if="promotion.coupon"
      class="bg-gray-50 border border-dashed border-brand-600 rounded-6 p-3 mt-4"
    >
      <p class="mb-2">{{ $t('promotion.enter_coupon') }}</p>
      <div class="relative flex justify-between items-center">
        <input
          class="w-full text-20 font-semibold outline-none border rounded-6 pl-4 pr-12 py-3 cursor-pointer"
          readonly
          :value="promotion.coupon"
          @click="copy"
        />
        <span
          class="absolute right-4 pointer-events-none"
          :class="copied ? 'text-green-800 font-semibold' : 'underline'"
        >
          <template v-if="copied">{{ $t('promotion.copied') }}</template>
          <template v-else>{{ $t('promotion.copy') }}</template>
        </span>
      </div>
    </div>
    <ul class="list-disc ml-5 mt-4 mb-6 leading-relaxed">
      <li>
        <template v-if="promotion.newCustomersOnly">
          {{
            $t('promotion.new_customers_only', {
              provider: providerDisplayName,
            })
          }}
        </template>
        <template v-else>
          {{ $t('promotion.all_customers', { provider: providerDisplayName }) }}
        </template>
      </li>
      <li v-for="detail in promotion.details || []" :key="detail">
        {{ $t(`promotions:${detail}`, promoVariables) }}
      </li>
      <li v-if="promotion.endDate">
        {{ $t('promotion.offer_ends') }}
        <NuxtTime
          class="font-semibold"
          :datetime="promotion.endDate"
          :locale="$lang"
          date-style="long"
        />
      </li>
    </ul>
    <p v-if="promotion.conditions">
      <strong>* {{ $t('promotion.promotion_conditions') }}</strong>
      <span v-for="condition in promotion.conditions" :key="condition">
        {{ $t(`promotions:${condition}`, promoVariables) }}
      </span>
    </p>
  </div>
</template>

<script lang="ts" setup>
import { CouponCopyClick } from '@core/analytics/core-events/coupon'
import { getPromotionVariables } from './helpers/utils'
import type { Promotion } from './types'

const props = defineProps<{
  providerDisplayName: string
  promotion: Promotion
}>()

const { $track } = useNuxtApp()

const copied = ref(false)

async function copy() {
  if (props.promotion.coupon) {
    await copyToClipboard(props.promotion.coupon)
    copied.value = true
    await $track(
      CouponCopyClick({
        couponCopy: {
          location: 'promotion_details',
          label: props.promotion.coupon,
          type: 'default',
        },
      }),
    )
    setTimeout(() => {
      copied.value = false
    }, 1000)
  }
}

const promoVariables = computed(() => {
  return getPromotionVariables(props.promotion, props.providerDisplayName)
})
</script>
